import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Handshake, Loader2, Check, X, ArrowRightLeft } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import NegotiationTimeline from "../vermittler/NegotiationTimeline";

/**
 * Mai-2026 — Händler-Panel für eingehende Vermittler-Provisionsvorschläge.
 * Erscheint unterhalb des SmartInbox-Aggregats in der Inbox-Tab.
 * Bietet Accept / Decline / Counter-Offer pro Vorschlag.
 */
export default function DealerReferralProposalsPanel() {
    const [proposals, setProposals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [counter, setCounter] = useState({});

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/dealer/referral-commission-proposals?status_filter=pending");
            setProposals(Array.isArray(data?.items) ? data.items : []);
        } catch {
            /* ignore */
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const decide = async (pid, decision, note) => {
        try {
            await api.post(`/dealer/referral-commission-proposals/${pid}/decide`, { decision, note: note || undefined });
            toast.success(decision === "accept" ? "Vorschlag angenommen — Provision aufs Inserat geschrieben." : "Vorschlag abgelehnt.");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Aktion fehlgeschlagen.");
        }
    };

    const sendCounter = async (pid) => {
        const state = counter[pid] || {};
        const amt = parseInt(state.amount, 10);
        if (!Number.isFinite(amt) || amt < 50 || amt > 500) {
            toast.error("Gegenvorschlag muss zwischen 50 € und 500 € liegen.");
            return;
        }
        try {
            await api.post(`/dealer/referral-commission-proposals/${pid}/counter`, {
                counter_commission_eur: amt,
                note: (state.note || "").trim() || undefined,
            });
            toast.success(`Gegenvorschlag (${amt} €) gesendet — Vermittler entscheidet.`);
            setCounter((c) => ({ ...c, [pid]: { ...c[pid], open: false } }));
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Gegenvorschlag fehlgeschlagen.");
        }
    };

    if (loading) {
        return <div className="mt-4 text-xs text-gray-500 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Lade Vermittler-Vorschläge…</div>;
    }
    if (proposals.length === 0) return null;

    return (
        <div className="mt-6 border-2 border-amber-300 bg-amber-50/40 rounded-sm p-4 space-y-3" data-testid="dealer-referral-proposals-panel">
            <div className="flex items-center gap-2">
                <Handshake className="w-5 h-5 text-amber-700" />
                <h3 className="font-display font-black text-base text-amber-900">Vermittler-Provisionsvorschläge ({proposals.length})</h3>
            </div>
            <ul className="space-y-3" data-testid="dealer-referral-proposals-list">
                {proposals.map((p) => {
                    const c = counter[p.id] || { open: false, amount: Math.max(50, Math.min(500, Math.round(p.proposed_commission_eur * 0.7))), note: "" };
                    return (
                        <li key={p.id} className="bg-white border border-gray-200 rounded-sm p-3" data-testid={`dealer-referral-proposal-${p.id}`}>
                            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm">{p.listing_title}</div>
                                    <div className="text-[11px] text-gray-500 mt-0.5">
                                        Vermittler: <strong>{p.vermittler_label}</strong> · Wunsch:
                                        <span className="font-mono-num font-bold text-amber-800 ml-1">{Number(p.proposed_commission_eur).toLocaleString("de-DE")} €</span>
                                    </div>
                                    {p.buyer_note && <div className="text-[11px] text-gray-700 mt-1 italic">„{p.buyer_note}"</div>}
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Button type="button" size="sm" onClick={() => decide(p.id, "accept")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`dealer-referral-accept-${p.id}`}>
                                        <Check className="w-3.5 h-3.5 mr-1" /> Annehmen
                                    </Button>
                                    <Button type="button" size="sm" variant="outline" onClick={() => setCounter((cc) => ({ ...cc, [p.id]: { ...c, open: !c.open } }))} className="border-violet-300 text-violet-800 hover:bg-violet-50" data-testid={`dealer-referral-counter-${p.id}`}>
                                        <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Gegenvorschlag
                                    </Button>
                                    <Button type="button" size="sm" variant="outline" onClick={() => decide(p.id, "decline")} className="border-red-300 text-red-700 hover:bg-red-50" data-testid={`dealer-referral-decline-${p.id}`}>
                                        <X className="w-3.5 h-3.5 mr-1" /> Ablehnen
                                    </Button>
                                </div>
                            </div>
                            {c.open && (
                                <div className="mt-3 pt-3 border-t border-violet-200 grid grid-cols-1 sm:grid-cols-[140px_1fr_auto] gap-2" data-testid={`dealer-referral-counter-form-${p.id}`}>
                                    <Input
                                        type="number"
                                        min={50}
                                        max={500}
                                        step={10}
                                        value={c.amount}
                                        onChange={(e) => setCounter((cc) => ({ ...cc, [p.id]: { ...c, amount: e.target.value } }))}
                                        className="rounded-sm font-mono-num"
                                        data-testid={`dealer-referral-counter-amount-${p.id}`}
                                        placeholder="z. B. 120"
                                    />
                                    <Input
                                        value={c.note || ""}
                                        onChange={(e) => setCounter((cc) => ({ ...cc, [p.id]: { ...c, note: e.target.value } }))}
                                        className="rounded-sm"
                                        placeholder='Hinweis (optional, z. B. "Mein Limit ist 120 €")'
                                        data-testid={`dealer-referral-counter-note-${p.id}`}
                                    />
                                    <Button type="button" size="sm" onClick={() => sendCounter(p.id)} className="bg-violet-700 hover:bg-violet-800 text-white" data-testid={`dealer-referral-counter-send-${p.id}`}>
                                        Senden
                                    </Button>
                                </div>
                            )}
                            {Array.isArray(p.negotiation_history) && p.negotiation_history.length > 1 && (
                                <details className="mt-3 pt-3 border-t border-gray-100" data-testid={`dealer-referral-history-toggle-${p.id}`}>
                                    <summary className="cursor-pointer text-[10px] font-bold uppercase tracking-wider text-gray-500 hover:text-[#111827]">
                                        Verhandlungs-Verlauf ({p.negotiation_history.length} Schritte)
                                    </summary>
                                    <div className="mt-2 bg-gray-50 border border-gray-100 rounded-sm p-2">
                                        <NegotiationTimeline history={p.negotiation_history} compact testId={`dealer-referral-history-${p.id}`} />
                                    </div>
                                </details>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
